import React from 'react';
import type { GetServerSidePropsContext } from 'next';
import { Trans } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { LegalContent } from 'src/components/legal/LegalContent';
import { LegalHeader } from 'src/components/legal/LegalHeader';
import LegalLayout from 'src/components/legal/LegalLayout';
import { styledTag } from 'src/utils/styled';

const TableWrapper = styledTag('div')`
    overflow-x: auto;
`;

const StyledTable = styledTag('table')`
    width: 100%;
    margin: 4rem 0;
    border-collapse: collapse;
`;

const StyledHeaderCell = styledTag('th')`
    border: 1px solid #ddd;
    padding: 5px;
    font-weight: bold;
    width: 33%;
`;

const StyledDataCell = styledTag('td')`
    border: 1px solid #ddd;
    padding: 5px;
    width: 33%;
`;

const ConfidentialityPage: PageComponent = () => (
    <>
        <LegalHeader>
            <Trans i18nKey="legal:confidentiality.title" />
        </LegalHeader>
        <LegalContent>
            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section1.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section1.content.paragraph1" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section1.content.paragraph2" />
                </p>
            </section>

            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section2.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph1" />
                </p>
                <TableWrapper>
                    <StyledTable>
                        <thead>
                            <tr>
                                <StyledHeaderCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.header.cell1" />
                                </StyledHeaderCell>
                                <StyledHeaderCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.header.cell2" />
                                </StyledHeaderCell>
                                <StyledHeaderCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.header.cell3" />
                                </StyledHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line4" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line5" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line6" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell1.line7" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell3.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row1.cell3.line2" />
                                    </p>
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line4" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line5" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line6" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line7" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell1.line8" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell3.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row2.cell3.line2" />
                                    </p>
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell1.line4" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row3.cell3" />
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line4" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line5" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line6" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell1.line7" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row4.cell3" />
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell1.line4" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row5.cell3" />
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell1.line3" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell1.line4" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell1.line5" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row6.cell3" />
                                </StyledDataCell>
                            </tr>
                            <tr>
                                <StyledDataCell>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row7.cell1.line1" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row7.cell1.line2" />
                                    </p>
                                    <p>
                                        <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row7.cell1.line3" />
                                    </p>
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row7.cell2" />
                                </StyledDataCell>
                                <StyledDataCell>
                                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph2.table.body.row7.cell3" />
                                </StyledDataCell>
                            </tr>
                        </tbody>
                    </StyledTable>
                </TableWrapper>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph3" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section2.content.paragraph4" />
                </p>
            </section>

            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section3.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section3.content.paragraph1" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section3.content.paragraph2" />
                </p>
            </section>

            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section4.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph1" />
                </p>
                <ul>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph2.listItem1" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph2.listItem2" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph2.listItem3" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph2.listItem4" />
                    </li>
                </ul>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph3" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section4.content.paragraph4" />
                </p>
            </section>

            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section5.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section5.content.paragraph1" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section5.content.paragraph2" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section5.content.paragraph3" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section5.content.paragraph4" />
                </p>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section5.content.paragraph5" />
                </p>
            </section>

            <section>
                <h2>
                    <Trans i18nKey="legal:confidentiality.content.section6.title" />
                </h2>
                <p>
                    <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph1" />
                </p>
                <ul>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem1" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem2" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem3" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem4" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem5" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem6" />
                    </li>
                    <li>
                        <Trans i18nKey="legal:confidentiality.content.section6.content.paragraph2.listItem7" />
                    </li>
                </ul>
                <p>
                    <Trans
                        i18nKey="legal:confidentiality.content.section6.content.paragraph3"
                        components={{
                            Link: (
                                <a href="mailto:contact@woopen.com" target="_blank" rel="noreferrer noopener">
                                    contact@woopen.com
                                </a>
                            ),
                        }}
                    />
                </p>
                <p>
                    <Trans
                        i18nKey="legal:confidentiality.content.section6.content.paragraph4"
                        components={{
                            Link: (
                                <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noreferrer noopener">
                                    https://www.cnil.fr/fr/plaintes
                                </a>
                            ),
                        }}
                    />
                </p>
            </section>

            <section>
                <Trans i18nKey="legal:confidentiality.lastUpdateDate" />
            </section>
        </LegalContent>
    </>
);

ConfidentialityPage.Layout = LegalLayout;

export const getServerSideProps = async (context: GetServerSidePropsContext) => ({
    props: {
        ...(await serverSideTranslations(context.locale as string, ['common', 'legal'])),
        SEOProps: {
            pageTitleTranslation: {
                key: 'common:seo.legal.confidentiality.title',
            },
        },
    },
});

export default ConfidentialityPage;
